import {
  Button,
  cn,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from 'component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IndicatorName, LABEL_FILTERS, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import {
  ProtectedAreasOsm,
  ProtectedAreasOsmRealm,
  ProtectedAreasOsmRealmMap,
} from '@/api/rest/resources/types/protectedAreas';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { ItemizedGraphTypeRenderer } from '@/components/Charts/components/ItemizedGraphType';
import { GRAPH_TYPE } from '@/components/Charts/hooks/useChartType';
import { NUM_ITEMS_TYPE } from '@/components/Charts/hooks/usePlotsForItemizedGraph';
import { ProtectedAreasChart } from '@/components/Charts/ProtectedAreas';
import { GraphFact } from '@/components/Charts/types';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { useSetIndicatorHasData } from '@/components/IndicatorCard/IndicatorContextProvider';
import { RADIUS_LABEL_MAPPING, RadiusType } from '@/components/MapOverview/components/MapRadiusContextProvider';
import { RADIUS_OPTIONS } from '@/components/MapOverview/components/ReportingBoundsLayer';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useProtectedAreasMethodology } from '@/hooks/useProtectedAreasMethodology';
import { useOrganizationIndicatorGraphFact } from '@/pages/shared/hooks/useOrganizationFact';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { NoData } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/components/NoData';
import { ProtectedAreasTable } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/protectedAreas/components/ProtectedAreasTable';

const CARD_VIEW = {
  GRAPH: 'graph',
  TABLE: 'table',
} as const;

type ProtectedAreasGraphTileType = Partial<GraphTileProps & { selectedBenchmark?: string | null }>;

export const ProtectedAreasGraphTile = (props: ProtectedAreasGraphTileType) => {
  const { t } = useTranslation();
  const [radius, setRadius] = useState<RadiusType>(RADIUS_OPTIONS.at(-1) ?? '30');
  const [selectedView, setSelectedView] = useState<(typeof CARD_VIEW)[keyof typeof CARD_VIEW]>(CARD_VIEW.GRAPH);
  const [activeRealm, setActiveRealm] = useState<ProtectedAreasOsmRealm>(ProtectedAreasOsmRealm.terrestrial);
  const { hasOsm } = useProtectedAreasMethodology();
  const realmFilter = hasOsm ? activeRealm : undefined;

  const iconByView = {
    [CARD_VIEW.GRAPH]: 'ri-bar-chart-line',
    [CARD_VIEW.TABLE]: 'ri-table-line',
  };

  return (
    <Stack center data-testid='protected-areas-graph-tile'>
      <Stack direction='row' spacing={2} center centerMain className='w-full justify-between mt-2'>
        <Stack className='h-full self-start'>
          <ToggleButtonGroup value={selectedView} onChange={setSelectedView} size='small'>
            {Object.values(CARD_VIEW).map((view) => (
              <ToggleButton key={view} value={view}>
                <i
                  className={
                    // eslint-disable-next-line security/detect-object-injection
                    `${iconByView[view]} mr-1`
                  }
                />
                {t(`shared.ncaDetail.details.protectedAreas.views.${view}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
        <Stack spacing={2}>
          <Stack direction='row' spacing={2} center centerMain>
            <span>{t('shared.projects.map.labels.radius')}</span>
            <Select value={radius} onValueChange={(value) => setRadius(value as RadiusType)}>
              <SelectTrigger>{`${radius} ${t('global.ui.unit.km')}`}</SelectTrigger>
              <SelectContent>
                {RADIUS_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {`${option} ${t('global.ui.unit.km')}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Stack>
        </Stack>
      </Stack>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-8 lg:pl-1'>
        {hasOsm && (
          <Stack className='w-full border-b border-divider' direction='row'>
            {Object.keys(ProtectedAreasOsmRealm).map((realm) => (
              <Button
                key={realm}
                variant='text'
                className={cn(
                  'px-4 py-2 rounded-none typography-body2 w-[200px]',
                  activeRealm === realm ? 'bg-primary-12 text-white' : 'bg-white text-text-secondary',
                )}
                onClick={() => setActiveRealm(realm as ProtectedAreasOsmRealm)}
              >
                <span className='capitalize'>
                  {t(`global.protectedAreas.realms.${realm as keyof typeof ProtectedAreasOsmRealm}`)}
                </span>
              </Button>
            ))}
          </Stack>
        )}
        {selectedView === CARD_VIEW.GRAPH && <GraphView {...props} radius={radius} realmFilter={realmFilter} />}
        {selectedView === CARD_VIEW.TABLE && <TableView {...props} radius={radius} realmFilter={realmFilter} />}
      </Stack>
    </Stack>
  );
};

const GraphView = ({
  plotGroupId,
  selectedBenchmark,
  radius,
  realmFilter,
  ...props
}: ProtectedAreasGraphTileType & { radius: RadiusType; realmFilter?: ProtectedAreasOsmRealm }) => {
  const { setIndicatorGraphData } = useSetIndicatorHasData();

  const indicator = GraphTileType.protected_areas;

  const organizationId = useOrganizationId();
  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, indicator);
  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmark);

  const { [IndicatorName.protected_areas_area]: protectedArasChartFact } = useOrganizationIndicatorGraphFact<
    GraphFact[]
  >(
    IndicatorName.protected_areas_area,
    null,
    {
      landscape_radius: RADIUS_LABEL_MAPPING[`${radius}`],
      protection_category: LABEL_FILTERS.ALL,
    },
    plotGroupId,
  );

  const noChartData = !protectedArasChartFact?.value?.length;

  useEffect(() => {
    setIndicatorGraphData({ graphData: !noChartData });
  }, [noChartData, setIndicatorGraphData]);

  if (noChartData) {
    return <NoData />;
  }

  let chartData = protectedArasChartFact?.value ?? [];

  if (realmFilter) {
    chartData = chartData.filter((fact) => {
      const category = fact.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.protection_category)
        ?.value as ProtectedAreasOsm;
      return ProtectedAreasOsmRealmMap[`${realmFilter}`].includes(category);
    });
  }

  return (
    <ItemizedGraphTypeRenderer
      graphType={GRAPH_TYPE.DEFAULT}
      indicator={GraphTileType.protected_areas}
      plotRenderType={props?.plotRenderType ?? NUM_ITEMS_TYPE.TOP_PLOTS}
      plotGroupId={plotGroupId ?? undefined}
      customBenchmark={activeCustomBenchmark}
      defaultGraphType={
        <ProtectedAreasChart
          data={chartData}
          customBenchmark={activeCustomBenchmark}
          data-testid='protected-areas-chart'
        />
      }
    />
  );
};

const TableView = ({
  plotGroupId,
  radius,
  realmFilter,
}: ProtectedAreasGraphTileType & { radius: RadiusType; realmFilter?: ProtectedAreasOsmRealm }) => {
  const { [IndicatorName.protected_areas_area]: protectedArasFact } = useOrganizationIndicatorGraphFact<GraphFact[]>(
    IndicatorName.protected_areas_area,
    null,
    {
      landscape_radius: RADIUS_LABEL_MAPPING[`${radius}`],
      protection_category: LABEL_FILTERS.ALL,
    },
    plotGroupId,
  );

  const { [IndicatorName.protected_areas_min_distance]: protectedArasMinDistanceFact } =
    useOrganizationIndicatorGraphFact<GraphFact[]>(
      IndicatorName.protected_areas_min_distance,
      null,
      {
        landscape_radius: RADIUS_LABEL_MAPPING[`${radius}`],
        protection_category: LABEL_FILTERS.ALL,
      },
      plotGroupId,
    );

  let factData = [...(protectedArasFact?.value ?? []), ...(protectedArasMinDistanceFact?.value ?? [])];

  if (realmFilter) {
    factData = factData.filter((fact) => {
      const category = fact.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.protection_category)
        ?.value as ProtectedAreasOsm;
      return ProtectedAreasOsmRealmMap[`${realmFilter}`].includes(category);
    });
  }

  const noChartData = !factData?.length;

  if (noChartData) {
    return <NoData />;
  }

  return (
    <div className='max-h-[400px] overflow-y-auto w-full bg-[#fbfbfb] rounded-2xl py-2 px-4'>
      <ProtectedAreasTable data={factData} />
    </div>
  );
};
